import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useUser } from '../contexts/UserContext';

function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { canCreateData } = useUser();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [duplicating, setDuplicating] = useState(false);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    fetchJob();
  }, [jobId]);

  const fetchJob = async () => {
    setLoading(true);
    try {
      const data = await api.getJob(jobId);
      setJob(data);
    } catch (err) {
      setError(err.message || 'Failed to load job');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async () => {
    if (duplicating) return;
    if (!window.confirm('Create a copy of this job with its quote line items?')) return;

    setDuplicating(true);
    try {
      const newJob = await api.duplicateJob(jobId);
      navigate(`/jobs/${newJob.id}`);
    } catch (err) {
      alert(err.message || 'Failed to duplicate job');
    } finally {
      setDuplicating(false);
    }
  };

  const handleAcceptQuote = async () => {
    if (accepting) return;
    if (!window.confirm('Accept this quote? This will mark it as approved by the client.')) return;

    setAccepting(true);
    try {
      await api.acceptQuote(jobId);
      // Refresh job data
      const updated = await api.getJob(jobId);
      setJob(updated);
    } catch (err) {
      alert(err.message || 'Failed to accept quote');
    } finally {
      setAccepting(false);
    }
  };

  // Format currency
  const formatCurrency = (amount, currency = '$') => {
    if (!amount) return `${currency}0.00`;
    return `${currency}${parseFloat(amount).toFixed(2)}`;
  };

  // Calculate quote total
  const getQuoteTotal = () => {
    if (!job?.quote_line_items?.length) return 0;
    return job.quote_line_items.reduce((sum, item) => {
      if (item.type === 'labor' || item.type === 'hourly' || item.type === 'daily_labor') {
        return sum + (parseFloat(item.hours || 0) * parseFloat(item.rate || 0));
      }
      return sum + parseFloat(item.cost || 0);
    }, 0);
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    const statusStyles = {
      draft: 'bg-slate-100 text-slate-700',
      sent: 'bg-blue-100 text-blue-800',
      signed: 'bg-green-100 text-green-800',
      accepted: 'bg-green-100 text-green-800',
      active: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
    };
    return statusStyles[status] || 'bg-slate-100 text-slate-700';
  };

  // Get quote status
  const getQuoteStatus = () => {
    if (!job?.quote_line_items?.length) return null;
    if (job.quote_accepted_at) return 'accepted';
    if (job.quote_sent_at) return 'sent';
    return 'draft';
  };

  if (loading) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-slate-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-xl text-center">
            <i className="fas fa-exclamation-circle text-3xl mb-3"></i>
            <p>{error}</p>
            <button
              onClick={() => navigate('/jobs')}
              className="mt-4 text-red-700 font-medium hover:underline"
            >
              Back to Jobs
            </button>
          </div>
        </div>
      </div>
    );
  }

  const quoteStatus = getQuoteStatus();
  const quoteTotal = getQuoteTotal();

  return (
    <div className="py-12 px-6 bg-background min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link
          to="/jobs"
          className="inline-flex items-center text-slate-600 hover:text-primary mb-6 transition"
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Back to Jobs
        </Link>

        {/* Job Header */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 mb-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              {job.job_number && (
                <p className="text-sm font-mono text-slate-500 mb-1">#{job.job_number.replace('JOB-', '')}</p>
              )}
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h1 className="text-2xl md:text-3xl font-black text-primary">{job.title}</h1>
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusBadge(job.status)}`}>
                  {job.status || 'Active'}
                </span>
                {canCreateData && (
                  <button
                    onClick={handleDuplicate}
                    disabled={duplicating}
                    className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition disabled:opacity-50"
                  >
                    <i className={`fas ${duplicating ? 'fa-spinner fa-spin' : 'fa-copy'}`}></i>
                    {duplicating ? 'Duplicating...' : 'Duplicate'}
                  </button>
                )}
              </div>
              {job.client_name && (
                <p className="text-slate-600 mb-1">
                  <i className="fas fa-user mr-2 text-slate-400"></i>
                  {job.client_name}
                </p>
              )}
              {job.address && (
                <p className="text-slate-500 text-sm mb-2">
                  <i className="fas fa-map-marker-alt mr-2 text-slate-400"></i>
                  {job.address}
                </p>
              )}
              {job.description && (
                <p className="text-slate-600 mt-3">{job.description}</p>
              )}
            </div>
            <div className="text-right">
              {job.initial_value && (
                <div className="mb-2">
                  <p className="text-sm text-slate-500">Initial Value</p>
                  <p className="text-xl font-bold text-primary">
                    {formatCurrency(job.initial_value)}
                  </p>
                </div>
              )}
              {job.scheduled_date && (
                <div>
                  <p className="text-sm text-slate-500">Scheduled</p>
                  <p className="text-sm font-medium text-slate-700">
                    {new Date(job.scheduled_date).toLocaleDateString()}
                    {job.scheduled_time && ` at ${job.scheduled_time}`}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quote Section */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-primary flex items-center">
              <i className="fas fa-file-invoice mr-2 text-secondary"></i>
              Quote
              {quoteStatus && (
                <span className={`ml-3 px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(quoteStatus)}`}>
                  {quoteStatus}
                </span>
              )}
            </h2>
            {!job.quote_sent_at ? (
              canCreateData ? (
                <Link
                  to={`/quotes/build/${jobId}`}
                  className="inline-flex items-center space-x-2 bg-secondary text-white px-4 py-2 rounded-lg font-medium hover:bg-orange-600 transition text-sm"
                >
                  <i className="fas fa-edit"></i>
                  <span>{job.quote_line_items?.length > 0 ? 'Edit Quote' : 'Create Quote'}</span>
                </Link>
              ) : (
                <span className="inline-flex items-center space-x-2 bg-slate-300 text-slate-500 px-4 py-2 rounded-lg font-medium text-sm cursor-not-allowed" title="Upgrade to Pro to create quotes">
                  <i className="fas fa-lock"></i>
                  <span>{job.quote_line_items?.length > 0 ? 'Edit Quote' : 'Create Quote'}</span>
                </span>
              )
            ) : (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center space-x-2 bg-slate-100 text-slate-500 px-4 py-2 rounded-lg font-medium text-sm cursor-not-allowed" title="Cannot edit sent quote">
                  <i className="fas fa-lock"></i>
                  <span>Quote Sent</span>
                </span>
                {!job.quote_accepted_at && canCreateData && (
                  <button
                    onClick={handleAcceptQuote}
                    disabled={accepting}
                    className="inline-flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition text-sm disabled:opacity-50"
                  >
                    {accepting ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        <span>Accepting...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check"></i>
                        <span>Accept Quote</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>

          {job.quote_line_items?.length > 0 ? (
            <>
              <div className="space-y-2 mb-4">
                {job.quote_line_items.slice(0, 5).map((item, index) => {
                  const isLabor = item.type === 'labor' || item.type === 'hourly' || item.type === 'daily_labor';
                  const itemTotal = isLabor
                    ? parseFloat(item.hours || 0) * parseFloat(item.rate || 0)
                    : parseFloat(item.cost || 0);
                  return (
                    <div key={item.id || index} className="flex justify-between items-center py-2 border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                          isLabor ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {isLabor ? 'Labor' : 'Material'}
                        </span>
                        <span className="text-slate-700">{item.description}</span>
                      </div>
                      <span className="font-medium text-slate-900">
                        {formatCurrency(itemTotal)}
                      </span>
                    </div>
                  );
                })}
                {job.quote_line_items.length > 5 && (
                  <p className="text-sm text-slate-500 pt-2">
                    +{job.quote_line_items.length - 5} more items
                  </p>
                )}
              </div>
              <div className="flex justify-between items-center pt-3 border-t-2 border-slate-200">
                <span className="font-bold text-slate-700">Total</span>
                <span className="text-xl font-bold text-primary">{formatCurrency(job.quote_total_with_tax || quoteTotal)}</span>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <i className="fas fa-file-invoice text-4xl text-slate-300 mb-3"></i>
              <p>No quote created yet</p>
              <p className="text-sm">Create a quote to send to your client</p>
            </div>
          )}
        </div>

        {/* Change Orders Section */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-primary flex items-center">
              <i className="fas fa-file-signature mr-2 text-secondary"></i>
              Change Orders
              {job.change_orders?.length > 0 && (
                <span className="ml-2 bg-slate-100 text-slate-700 px-2 py-0.5 text-xs font-semibold rounded-full">
                  {job.change_orders.length}
                </span>
              )}
            </h2>
          </div>

          {job.change_orders?.length > 0 ? (
            <div className="space-y-3">
              {job.change_orders.map((order) => (
                <div key={order.id} className="flex justify-between items-center p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">{order.title || 'Change Order'}</p>
                    <p className="text-sm text-slate-500">
                      {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(order.status)}`}>
                      {order.status}
                    </span>
                    <span className="font-bold text-primary">
                      {formatCurrency(order.total)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <i className="fas fa-file-signature text-4xl text-slate-300 mb-3"></i>
              <p>No change orders</p>
              <p className="text-sm">Change orders are created in the mobile app</p>
            </div>
          )}
        </div>

        {/* Invoice Section */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          {(() => {
            // Find active (non-voided) invoice
            const activeInvoice = job.invoices?.find(inv => inv.status !== 'voided');
            const hasActiveInvoice = !!activeInvoice;
            const displayInvoice = activeInvoice || job.invoices?.[0];

            return (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-primary flex items-center">
                    <i className="fas fa-receipt mr-2 text-secondary"></i>
                    Invoice
                    {displayInvoice && (
                      <span className={`ml-3 px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(displayInvoice.status)}`}>
                        {displayInvoice.status}
                      </span>
                    )}
                  </h2>
                  <div className="flex gap-2">
                    {displayInvoice && (
                      <Link
                        to={`/invoices/${displayInvoice.id}`}
                        className="inline-flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-primary/90 transition text-sm"
                      >
                        <i className="fas fa-eye"></i>
                        <span>View Invoice</span>
                      </Link>
                    )}
                    {!hasActiveInvoice && (canCreateData ? (
                      <Link
                        to={`/invoices/build/${jobId}`}
                        className="inline-flex items-center space-x-2 bg-secondary text-white px-4 py-2 rounded-lg font-medium hover:bg-orange-600 transition text-sm"
                      >
                        <i className="fas fa-plus"></i>
                        <span>Create Invoice</span>
                      </Link>
                    ) : (
                      <span className="inline-flex items-center space-x-2 bg-slate-300 text-slate-500 px-4 py-2 rounded-lg font-medium text-sm cursor-not-allowed" title="Upgrade to Pro to create invoices">
                        <i className="fas fa-lock"></i>
                        <span>Create Invoice</span>
                      </span>
                    ))}
                  </div>
                </div>

                {displayInvoice ? (
                  <Link to={`/invoices/${displayInvoice.id}`} className="block">
                    <div className={`flex justify-between items-center p-4 rounded-lg hover:bg-slate-100 transition ${displayInvoice.status === 'voided' ? 'bg-red-50' : 'bg-slate-50'}`}>
                      <div>
                        <p className={`font-medium ${displayInvoice.status === 'voided' ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                          Invoice #{displayInvoice.invoice_number || displayInvoice.id.slice(0, 8)}
                        </p>
                        <p className="text-sm text-slate-500">
                          {new Date(displayInvoice.created_at).toLocaleDateString()}
                          {displayInvoice.status === 'voided' && <span className="ml-2 text-red-600">(Voided)</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`font-bold text-lg ${displayInvoice.status === 'voided' ? 'text-slate-400 line-through' : 'text-primary'}`}>
                          {formatCurrency(displayInvoice.total_with_tax || displayInvoice.total)}
                        </span>
                        <i className="fas fa-chevron-right text-slate-400"></i>
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <i className="fas fa-receipt text-4xl text-slate-300 mb-3"></i>
                    <p>No invoice yet</p>
                    <p className="text-sm">Create an invoice when the job is complete</p>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

export default JobDetail;
